import React, { memo, useMemo } from 'react';

import classnames from 'classnames';

import { CALENDAR_VIEW_TYPES } from 'constants/calendar';
import { DATE_FORMATS } from 'constants/index';

import { getMonthYearForIndex } from 'utils/calendar';
import { createDate } from 'utils/date';

import classes from './Cells.module.scss';
import DayCell from './DayCell';
import MonthCell from './MonthCell';

const Cells = memo(({ viewType, rangeStartDate, production, months, entityPrefDates, isCastingView }) => {
  const sortedDates = useMemo(() => {
    const dates =
      entityPrefDates?.length && isCastingView
        ? entityPrefDates
        : production?.performances?.map(d => ({
            startDate: d.startDate,
            id: d.id,
            isCancelled: d.isCancelled,
            startTime: d.startTime,
          }));
    return dates?.sort((a, b) => createDate(a.startDate).diff(createDate(b.startDate), 'day')) || [];
  }, [entityPrefDates, production?.performances, isCastingView]);

  // NOTE: Format date range text
  const formatDateRangeText = useMemo(() => {
    if (!sortedDates || sortedDates.length === 0) {
      return '';
    }

    const firstDate = createDate(sortedDates[0].startDate);
    const lastDate = createDate(sortedDates[sortedDates.length - 1].startDate);

    const startFormatted = `${firstDate.format(DATE_FORMATS.DATE)} ${firstDate
      .format(DATE_FORMATS.MONTH_ABBR)
      .toUpperCase()} '${firstDate.format(DATE_FORMATS.YEAR_SHORT)}`;

    if (firstDate.isSame(lastDate, 'day')) {
      return startFormatted;
    }

    const endFormatted = `${lastDate.format(DATE_FORMATS.DATE)} ${lastDate
      .format(DATE_FORMATS.MONTH_ABBR)
      .toUpperCase()} '${lastDate.format(DATE_FORMATS.YEAR_SHORT)}`;

    return `${startFormatted} - ${endFormatted}`;
  }, [sortedDates]);

  const dayCells = useMemo(() => {
    if (![CALENDAR_VIEW_TYPES.MONTH, CALENDAR_VIEW_TYPES.FIFTEEN_DAYS].includes(viewType)) {
      return null;
    }

    const daysCount = viewType === CALENDAR_VIEW_TYPES.FIFTEEN_DAYS ? 15 : 30;
    return Array.from({ length: daysCount }).map((_, index) => {
      const currentDate = rangeStartDate.add(index, 'day');
      const cellDate = createDate(`${currentDate.year()}-${currentDate.month() + 1}-${currentDate.date()}`);
      return (
        <td key={index} className={classes.dayCell}>
          <DayCell
            cellDate={cellDate}
            day={cellDate.date()}
            performedDates={sortedDates}
            productionId={production?.id}
            viewType={viewType}
            dayIndex={index}
            startTime={sortedDates[index]?.startTime}
          />
          {index === 0 && formatDateRangeText && <div className={classes.dateRangeText}>{formatDateRangeText}</div>}
        </td>
      );
    });
  }, [viewType, rangeStartDate, sortedDates, production?.id, formatDateRangeText]);

  const monthCells = useMemo(() => {
    if ([CALENDAR_VIEW_TYPES.MONTH, CALENDAR_VIEW_TYPES.FIFTEEN_DAYS].includes(viewType)) {
      return null;
    }

    return months.map((month, i) => {
      const { monthIndex, yearValue } = getMonthYearForIndex(rangeStartDate, i, viewType);

      return (
        <td
          key={i}
          className={classnames(classes.months, {
            [classes.monthsLast]: month === 'Dec' && i !== months.length - 1,
          })}
        >
          <MonthCell
            productionId={production?.id}
            month={monthIndex}
            year={yearValue}
            viewType={viewType}
            performedDates={sortedDates}
          />
          {i === 0 && formatDateRangeText && <div className={classes.dateRangeText}>{formatDateRangeText}</div>}
        </td>
      );
    });
  }, [viewType, months, rangeStartDate, sortedDates, production?.id, formatDateRangeText]);

  if ([CALENDAR_VIEW_TYPES.MONTH, CALENDAR_VIEW_TYPES.FIFTEEN_DAYS].includes(viewType)) {
    return dayCells;
  }

  return monthCells;
});

export default Cells;
